import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { API_URL } from '../config';

function TShirtMesh({ imageUrl, shirtColor = '#1a1a1a' }) {
  const meshRef = useRef();
  const [texture, setTexture] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Load texture manually to avoid crashes
  useEffect(() => {
    if (!imageUrl) {
      setError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);

    let isCancelled = false;
    const loader = new THREE.TextureLoader();

    // Determine URL to load
    let urlToLoad = imageUrl;
    
    // Only proxy external URLs (OpenAI), not our own R2 URLs or data URLs
    const needsProxy = 
      !imageUrl.startsWith('blob:') && 
      !imageUrl.startsWith('data:') &&
      !imageUrl.startsWith(API_URL.replace('/api/v1', '/images/'));

    if (needsProxy) {
      // Proxy through backend for CORS (OpenAI URLs)
      urlToLoad = `${API_URL}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    }

    loader.load(
      urlToLoad,
      (loadedTexture) => {
        if (!isCancelled) {
          loadedTexture.wrapS = THREE.ClampToEdgeWrapping;
          loadedTexture.wrapT = THREE.ClampToEdgeWrapping;
          loadedTexture.center.set(0.5, 0.5);
          loadedTexture.needsUpdate = true;
          setTexture(loadedTexture);
          setLoading(false);
          setError(false);
          console.log('✅ T-shirt texture loaded successfully');
        }
      },
      (progress) => {
        console.log('Loading texture:', Math.round((progress.loaded / progress.total) * 100) + '%');
      },
      (err) => {
        console.error('❌ Texture load error:', err);
        if (!isCancelled) {
          setError(true);
          setLoading(false);
        }
      }
    );

    return () => {
      isCancelled = true;
      if (texture) {
        texture.dispose();
      }
    };
  }, [imageUrl]); // eslint-disable-line

  // More realistic t-shirt shape
  const tshirtShape = useMemo(() => {
    const shape = new THREE.Shape();
    
    shape.moveTo(-0.4, 2.0);
    shape.lineTo(-0.9, 1.85);
    shape.lineTo(-1.6, 1.6);
    shape.lineTo(-2.1, 1.0);
    shape.lineTo(-2.15, 0.7);
    shape.lineTo(-2.0, 0.5);
    shape.quadraticCurveTo(-1.7, 0.8, -1.3, 1.1);
    shape.lineTo(-1.35, -1.5);
    shape.lineTo(-1.25, -2.0);
    shape.quadraticCurveTo(0, -2.05, 1.25, -2.0);
    shape.lineTo(1.35, -1.5);
    shape.lineTo(1.3, 1.1);
    shape.quadraticCurveTo(1.7, 0.8, 2.0, 0.5);
    shape.lineTo(2.15, 0.7);
    shape.lineTo(2.1, 1.0);
    shape.lineTo(1.6, 1.6);
    shape.lineTo(0.9, 1.85);
    shape.lineTo(0.4, 2.0);
    shape.quadraticCurveTo(0, 2.1, -0.4, 2.0);
    
    return shape;
  }, []);

  // Create fabric texture
  const fabricTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = shirtColor;
    ctx.fillRect(0, 0, 512, 512);
    
    const imageData = ctx.getImageData(0, 0, 512, 512);
    const data = imageData.data;
    
    for (let i = 0; i < data.length; i += 4) {
      const noise = (Math.random() - 0.5) * 8;
      data[i] += noise;
      data[i + 1] += noise;
      data[i + 2] += noise;
    }
    
    ctx.putImageData(imageData, 0, 0);
    
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(2, 2);
    
    return tex;
  }, [shirtColor]);

  // Gentle idle animation
  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.2) * 0.08;
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.03;
    }
  });

  const extrudeSettings = {
    depth: 0.12,
    bevelEnabled: true,
    bevelThickness: 0.03,
    bevelSize: 0.025,
    bevelSegments: 5,
  };

  if (error) {
    return (
      <mesh>
        <boxGeometry args={[2, 3, 0.5]} />
        <meshStandardMaterial 
          color="#8b5cf6" 
          roughness={0.3}
          metalness={0.1}
        />
      </mesh>
    );
  }

  return (
    <group ref={meshRef}>
      {/* T-shirt body */}
      <mesh castShadow receiveShadow>
        <extrudeGeometry args={[tshirtShape, extrudeSettings]} />
        <meshStandardMaterial 
          map={fabricTexture}
          roughness={0.85}
          metalness={0.0}
          color={shirtColor}
        />
      </mesh>
      
      {/* Design print on front - ALWAYS render if we have texture */}
      {texture ? (
        <mesh position={[0, 0.2, 0.15]} castShadow>
          <planeGeometry args={[1.8, 1.8]} />
          <meshStandardMaterial
            map={texture}
            transparent
            roughness={0.6}
            metalness={0.0}
            side={THREE.FrontSide}
            toneMapped={false}
          />
        </mesh>
      ) : loading ? (
        <mesh position={[0, 0.2, 0.15]}>
          <planeGeometry args={[1.8, 1.8]} />
          <meshStandardMaterial
            color="#8b5cf6"
            transparent
            opacity={0.5}
            emissive="#8b5cf6"
            emissiveIntensity={0.3}
          />
        </mesh>
      ) : null}
    </group>
  );
}

// Simple ground plane
function GroundPlane() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2.5, 0]} receiveShadow>
      <planeGeometry args={[20, 20]} />
      <meshStandardMaterial color="#e0e0e0" />
    </mesh>
  );
}

function DragHint({ visible }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '2.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.4rem',
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.8s ease',
        zIndex: 10,
      }}
    >
      {/* Animated arc + hand */}
      <div style={{ position: 'relative', width: '64px', height: '56px' }}>
        {/* Arc SVG */}
        <svg
          width="64"
          height="56"
          viewBox="0 0 64 56"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            animation: 'dragArcSpin 1.6s ease-in-out infinite alternate',
          }}
        >
          <path
            d="M 8 40 A 26 26 0 0 1 56 40"
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="6 4"
          />
          {/* Left arrowhead */}
          <polyline
            points="8,40 14,36 13,43"
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Right arrowhead */}
          <polyline
            points="56,40 50,36 51,43"
            fill="none"
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {/* Hand cursor */}
        <div
          style={{
            position: 'absolute',
            bottom: '2px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '1.6rem',
            animation: 'dragHandRock 1.6s ease-in-out infinite alternate',
            transformOrigin: 'center bottom',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
          }}
        >
          ✋
        </div>
      </div>
      <span
        style={{
          fontSize: '0.72rem',
          fontWeight: 600,
          letterSpacing: '0.06em',
          color: 'rgba(255,255,255,0.9)',
          textTransform: 'uppercase',
          textShadow: '0 1px 4px rgba(0,0,0,0.7)',
          background: 'rgba(0,0,0,0.35)',
          padding: '2px 10px',
          borderRadius: '99px',
        }}
      >
        Drag to rotate
      </span>
      <style>{`
        @keyframes dragHandRock {
          0%   { transform: translateX(calc(-50% - 10px)) rotate(-18deg); }
          100% { transform: translateX(calc(-50% + 10px)) rotate(18deg); }
        }
        @keyframes dragArcSpin {
          0%   { transform: rotate(-8deg); }
          100% { transform: rotate(8deg); }
        }
      `}</style>
    </div>
  );
}

function TShirtViewer({ imageUrl }) {
  const [loadError, setLoadError] = useState(false);
  const [shirtColor, setShirtColor] = useState('#1a1a1a');
  const [showDragHint, setShowDragHint] = useState(false);
  const hintTimerRef = useRef(null);

  // Show drag hint for 3.5 seconds when a new image loads
  useEffect(() => {
    if (!imageUrl) return;
    clearTimeout(hintTimerRef.current);
    setShowDragHint(true);
    hintTimerRef.current = setTimeout(() => setShowDragHint(false), 3500);
    return () => clearTimeout(hintTimerRef.current);
  }, [imageUrl]);

  const shirtColors = [
    { name: 'Black', value: '#1a1a1a' },
    { name: 'White', value: '#f5f5f5' },
    { name: 'Navy', value: '#1e3a5f' },
    { name: 'Gray', value: '#6b7280' },
    { name: 'Olive', value: '#4a5d3f' },
    { name: 'Burgundy', value: '#6b2737' },
  ];

  if (!imageUrl) {
    return (
      <div className="tshirt-viewer-container">
        <div style={{padding: '3rem', textAlign: 'center', color: 'var(--text-muted)'}}>
          <p>🎨 Generate an image first or check the gallery!</p>
          <p style={{fontSize: '0.9rem', marginTop: '1rem', opacity: 0.7}}>
            The most recent design will load automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="tshirt-viewer-container fade-in">
      {/* T-shirt color selector */}
      <div className="shirt-color-picker">
        <label style={{fontWeight: 600, marginBottom: '0.5rem', display: 'block'}}>
          👕 Shirt Color:
        </label>
        <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap'}}>
          {shirtColors.map(color => (
            <button
              key={color.value}
              onClick={() => setShirtColor(color.value)}
              className={`color-chip ${shirtColor === color.value ? 'active' : ''}`}
              style={{
                background: color.value,
                width: '48px',
                height: '48px',
                border: shirtColor === color.value ? '3px solid var(--accent)' : '2px solid var(--border)',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: shirtColor === color.value ? '0 0 0 4px rgba(139, 92, 246, 0.2)' : 'none',
              }}
              title={color.name}
              aria-label={`Select ${color.name} shirt`}
            />
          ))}
        </div>
      </div>

      <div 
        className="tshirt-canvas" 
        style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          minHeight: '500px', 
          borderRadius: '16px', 
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <DragHint visible={showDragHint} />
        <Canvas
          shadows
          camera={{ position: [0, 0, 6], fov: 40 }}
          gl={{ 
            antialias: true, 
            alpha: false,
            powerPreference: 'high-performance',
          }}
          onError={(error) => {
            console.error('Canvas error:', error);
            setLoadError(true);
          }}
        >
          <color attach="background" args={['#f0f0f0']} />
          
          {/* Lighting - simpler setup to avoid crashes */}
          <ambientLight intensity={0.5} />
          <directionalLight 
            position={[5, 8, 5]} 
            intensity={1.0} 
            castShadow
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
          />
          <directionalLight position={[-5, 3, 3]} intensity={0.4} />
          <pointLight position={[0, 5, -5]} intensity={0.3} />
          
          <React.Suspense 
            fallback={
              <mesh>
                <boxGeometry args={[1.5, 2, 0.3]} />
                <meshStandardMaterial color="#8b5cf6" roughness={0.3} />
              </mesh>
            }
          >
            <TShirtMesh imageUrl={imageUrl} shirtColor={shirtColor} />
          </React.Suspense>
          
          <GroundPlane />
          
          <OrbitControls
            enableZoom={true}
            enablePan={false}
            minDistance={4}
            maxDistance={10}
            maxPolarAngle={Math.PI / 1.8}
            minPolarAngle={Math.PI / 4}
          />
        </Canvas>
      </div>
      
      {loadError && (
        <div style={{
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          color: '#ef4444', 
          background: 'rgba(26, 26, 26, 0.95)', 
          padding: '1.5rem', 
          borderRadius: '12px',
          textAlign: 'center',
          backdropFilter: 'blur(10px)',
          zIndex: 10,
        }}>
          <p style={{fontSize: '1.1rem', fontWeight: 600}}>⚠️ 3D Viewer Error</p>
          <p style={{fontSize: '0.85rem', marginTop: '0.5rem', opacity: 0.8}}>
            Try refreshing the page or using a different browser.
          </p>
        </div>
      )}
      
      <p className="drag-hint" style={{marginTop: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem'}}>
        🖱️ Drag to rotate · Scroll to zoom · Choose shirt color above
      </p>
    </div>
  );
}

export default TShirtViewer;
